import util from '../../lib/util';
import {ReactNode} from "react";
import {SelectFilterOptionFunc} from "./Select";

/**
 * We are given a candidate with a structure of label/value/data. We then test the input string
 * to see if we have a match.  To make this useful, we also pass a comparator function that will
 * be used.
 *
 * We will tokenize the input and candidate's label and value to pass to the comparator
 *
 * @param {Function} comparator
 */
function createFilterOption(comparator: Function): SelectFilterOptionFunc {
    return (candidate: any, input: any) => {
        return comparator(candidate, input);
    };
}

/**
 * Split an input by spaces and commas
 *
 * @param {string} input
 * @return {string[]}
 */
function split(input: string): string[] {
    if (!input) return [];
    else return util.flatten(input.split(' ').map(term => term.split(',')))
                    .map(term => term.trim())
                    .filter(util.existsFilter)
                    .map(term => term.replace(/['"]+/g, ''))
                    .filter(util.distinctFilter);
}

/**
 * Used for comparing contacts with their names.
 *
 * @param {string | {label: ReactNode, value: string, data: any}} candidate
 * @param {string} input
 * @return {boolean}
 */
export function contactComparator(candidate: string | { label: ReactNode; value: string; data: any; }, input: string): boolean {
    // We need the data from the candidate.  If we don't have contact or data, we need to use the value.
    // @ts-ignore
    const data = typeof(candidate) === 'object' ? candidate?.data?.contact : null;
    if (data === null) return false;

    // Now check to see if the input terms match the candidate's contact (or use value)
    let candidateTerms = data // If we have data, the terms are just the first and last name, plus the aliases
                         ? [data.first, data.middle, data.last, data.nick]
                             .concat(split(data.aliases))
                             .filter(util.existsFilter)
                             .filter(util.distinctFilter)
                         : (candidate as any)?.label // otherwise use the value
                           ? split((candidate as any).label)
                           : [];

    candidateTerms = candidateTerms.map(t => t.toLowerCase());

    // If there are multiple input terms, we need to match EACH of them with some term in the candidate terms
    const inputTerms = split(input).map(t => t.toLowerCase());

    return inputTerms.every(term => candidateTerms.some(cd => cd.includes(term)));
}

export default createFilterOption;
